import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { UserContext } from '../../../context/UserContext';
import CustomerModal from '../CustomerComponents/CustomerModal';
import QRModal from '../CustomerComponents/QRModal';
import {
  CheckCircle2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  MoreVertical, QrCode, XCircle,
} from 'lucide-react';
import './PreInscripcionesPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

// ── AuthImage ─────────────────────────────────────────────────────────────────
// Fetches an API path with the Bearer token and creates a local blob URL so
// <img> tags work even for auth-protected endpoints.

function useAuthImage(path, token) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!path) { setSrc(null); return; }

    const url = buildUrl(path);
    if (!url) { setSrc(null); return; }

    // Absolute external URL (e.g. old presigned S3) — use directly
    if (path.startsWith('http://') || path.startsWith('https://')) {
      setSrc(url);
      return;
    }

    // Relative API path — fetch with auth header
    let blobUrl = null;
    const controller = new AbortController();
    setLoading(true);

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!blob || !blob.size) { setSrc(null); return; }
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      })
      .catch(() => setSrc(null))
      .finally(() => setLoading(false));

    return () => {
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return { src, loading };
}

// ── Small image thumbnail ──────────────────────────────────────────────────────

function ImageThumb({ path, token, alt, onOpen }) {
  const { src, loading } = useAuthImage(path, token);

  if (!path) return <span className="text-gray-300 text-xs">—</span>;
  if (loading) return <div className="w-10 h-10 rounded-lg bg-gray-100 animate-pulse flex-shrink-0" />;
  if (!src) return <span className="text-gray-400 text-xs">✗</span>;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen({ path, alt });
      }}
      className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-lg"
      title={`Ver ${alt}`}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="pointer-events-none w-10 h-10 rounded-lg object-cover border border-gray-200 hover:opacity-80 hover:border-green-400 transition-all cursor-zoom-in"
      />
    </button>
  );
}

// ── Photo avatar (round, with click to open) ──────────────────────────────────

function AvatarThumb({ path, token, name, onOpen }) {
  const { src, loading } = useAuthImage(path, token);

  if (loading) return <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />;

  if (src) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen({ path, alt: name });
        }}
        className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-full"
        title="Ver foto"
      >
        <img
          src={src}
          alt={name}
          draggable={false}
          className="pointer-events-none w-9 h-9 rounded-full object-cover border border-gray-200 hover:opacity-80 hover:border-green-400 transition-all cursor-zoom-in"
        />
      </button>
    );
  }

  return (
    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0 text-sm">
      👤
    </div>
  );
}

// ── Modal vista documento / foto ───────────────────────────────────────────────

function DocumentPreviewModal({ preview, token, onClose }) {
  const { path, alt } = preview || {};
  const { src, loading } = useAuthImage(path, token);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [preview, onClose]);

  if (!preview || typeof document === 'undefined' || !document.body) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || 'Vista de documento'}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900 truncate">{alt || 'Documento'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cerrar
          </button>
        </div>
        <div className="flex items-center justify-center bg-gray-50 p-4 min-h-[240px] max-h-[calc(90vh-64px)] overflow-auto">
          {loading ? (
            <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
          ) : src ? (
            <img
              src={src}
              alt={alt || 'Documento'}
              draggable={false}
              className="max-w-full max-h-[calc(90vh-120px)] object-contain rounded-lg"
            />
          ) : (
            <p className="text-sm text-gray-500">No se pudo cargar la imagen.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Estado ────────────────────────────────────────────────────────────────────

const STATUS_LABEL = { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' };

function StatusBadge({ status }) {
  return (
    <span className={`pre-status pre-status--${status || 'pending'}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ── Menú de acciones de la fila ───────────────────────────────────────────────

function RowActions({ item, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Aprobar y rechazar solo son válidos mientras está pendiente: el backend
  // responde 400 "already processed" en cualquier otro estado. Una vez resuelta
  // no queda ninguna acción, así que la fila no muestra menú.
  if (item.status !== 'pending') return <span className="pre-date">—</span>;

  return (
    <div className="pre-actions" ref={ref}>
      <button
        type="button"
        className="pre-icon-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Acciones de ${item.first_name} ${item.last_name}`}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="pre-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="pre-menu-item"
            onClick={() => { setOpen(false); onApprove(item); }}
          >
            <CheckCircle2 size={16} /> Aprobar
          </button>
          <span className="pre-menu-sep" />
          <button
            type="button"
            role="menuitem"
            className="pre-menu-item pre-menu-item--danger"
            onClick={() => { setOpen(false); onReject(item); }}
          >
            <XCircle size={16} /> Rechazar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

/** Filas por página. El listado no pagina en servidor, así que se trocea aquí. */
const PAGE_SIZE = 11;

const TABS = [
  { value: '',         label: 'Todas' },
  { value: 'approved', label: 'Aprobadas' },
  { value: 'pending',  label: 'Pendientes' },
  { value: 'rejected', label: 'Rechazadas' },
];

export default function PreInscripcionesPage() {
  const [token, , , venueId] = useContext(UserContext);

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('');
  const [error,   setError]   = useState('');
  const [page,    setPage]    = useState(1);
  const [toast,   setToast]   = useState(null);
  const [sociosCap, setSociosCap] = useState(null);

  // Vista ampliada de foto / DNI
  const [docPreview, setDocPreview] = useState(null);
  const openDocPreview = useCallback((preview) => setDocPreview(preview), []);
  const closeDocPreview = useCallback(() => setDocPreview(null), []);

  // QR de preinscripción
  const [qrModalOpen, setQrModalOpen] = useState(false);

  // Create socio modal
  const [createModalOpen,    setCreateModalOpen]    = useState(false);
  const [prefillClientData,  setPrefillClientData]  = useState(null);
  const [creatingFromPreReg, setCreatingFromPreReg] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const showToast = useCallback((text, kind = 'ok') => {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const refreshPendingBadge = async () => {
    if (!venueId) return;
    try {
      const response = await axios.get(`/v1/venues/${venueId}/pre-registrations/count`, {
        headers: { ...headers, 'Cache-Control': 'no-cache, no-store, max-age=0', Pragma: 'no-cache' },
        params: { _ts: Date.now() },
      });
      window.dispatchEvent(new CustomEvent('prereg-count-updated', {
        detail: { pending: response.data.pending || 0 },
      }));
    } catch {
      window.dispatchEvent(new CustomEvent('prereg-count-updated'));
    }
  };

  const fetchItems = async () => {
    if (!venueId) return;
    setLoading(true); setError('');
    try {
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`/v1/venues/${venueId}/pre-registrations`, { headers, params });
      setItems(Array.isArray(r.data) ? r.data : []);
      refreshPendingBadge();
    } catch {
      setError('Error al cargar las preinscripciones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [venueId, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contador "47/120 socios" — mismo origen que en Socios.
  useEffect(() => {
    if (!token || !venueId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/v1/venues/${venueId}`, { headers });
        if (!cancelled && (res.data.max_socios != null || res.data.socios_count != null)) {
          setSociosCap({
            socios_count: res.data.socios_count ?? 0,
            max_socios: res.data.max_socios ?? null,
          });
        }
      } catch {
        if (!cancelled) setSociosCap(null);
      }
    })();
    return () => { cancelled = true; };
  }, [token, venueId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambiar de pestaña se vuelve a la primera página.
  useEffect(() => { setPage(1); }, [filter]);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleItems = useMemo(
    () => items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [items, currentPage]
  );

  const openCreateFromPreRegistration = (item) => {
    setCreatingFromPreReg(item);
    setPrefillClientData({
      preRegistrationId: item.id,
      nombre:    item.first_name,
      apellidos: item.last_name,
      dni:       item.dni || '',
      telefono:  item.phone || '',
      email:     item.email || '',
      direccion: item.address || '',
      fecha_nacimiento: item.birth_date
        ? (() => { const [fy, fm, fd] = item.birth_date.split('-'); return `${fd}/${fm}/${fy}`; })()
        : '',
      photo_path:     item.photo_path     || null,
      dni_front_path: item.dni_front_path || null,
      dni_back_path:  item.dni_back_path  || null,
    });
    setCreateModalOpen(true);
  };

  const handleReject = async (item) => {
    // El backend BORRA la preinscripción al rechazar (no la marca), así que
    // se avisa de que no hay vuelta atrás.
    if (!window.confirm('¿Rechazar esta preinscripción? Se eliminará y no se podrá recuperar.')) return;
    try {
      await axios.put(`/v1/venues/${venueId}/pre-registrations/${item.id}/reject`, {}, { headers });
      await fetchItems();
      showToast('Solicitud de inscripción rechazada');
    } catch {
      setError('Error al rechazar la preinscripción.');
    }
  };

  const capLabel = sociosCap && sociosCap.max_socios != null
    ? `${sociosCap.socios_count}/${sociosCap.max_socios} socios`
    : sociosCap
    ? `${sociosCap.socios_count} socios`
    : null;
  const capFull = sociosCap?.max_socios != null && sociosCap.socios_count >= sociosCap.max_socios;

  return (
    <>
      <div className="pre-page">
        <div className="pre-bar">
          <h1 className="pre-title">Preinscripciones</h1>
          {capLabel && (
            <span className={`pre-count${capFull ? ' is-full' : ''}`}>{capLabel}</span>
          )}
          <span className="pre-bar-spacer" />
          <button type="button" className="pre-btn pre-btn--primary" onClick={() => setQrModalOpen(true)}>
            <QrCode size={16} aria-hidden="true" />
            QR Preinscripción
          </button>
        </div>

        <div className="pre-tabs" role="tablist" aria-label="Filtrar preinscripciones">
          {TABS.map((tab) => (
            <button
              key={tab.value || 'all'}
              type="button"
              role="tab"
              aria-selected={filter === tab.value}
              className={`pre-tab${filter === tab.value ? ' is-active' : ''}`}
              onClick={() => setFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && <div className="pre-error" role="alert">{error}</div>}

        <div className="pre-surface">
          {loading ? (
            <div className="pre-state">Cargando preinscripciones…</div>
          ) : items.length === 0 ? (
            <div className="pre-state">
              No hay preinscripciones{filter ? ` ${TABS.find((t) => t.value === filter).label.toLowerCase()}` : ''}.
            </div>
          ) : (
            <>
              <div className="pre-scroll">
                <table className="pre-table">
                  <thead>
                    <tr>
                      <th>Solicitante</th>
                      <th>DNI</th>
                      <th>Contacto</th>
                      <th>Documentos</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="pre-applicant">
                            <AvatarThumb
                              path={item.photo_path}
                              token={token}
                              name={`${item.first_name} ${item.last_name}`}
                              onOpen={openDocPreview}
                            />
                            <div>
                              <p className="pre-applicant-name">{item.first_name} {item.last_name}</p>
                              {item.address && <p className="pre-applicant-sub">{item.address}</p>}
                            </div>
                          </div>
                        </td>

                        <td className="pre-dni">{item.dni || '—'}</td>

                        <td>
                          <div className="pre-contact">
                            {item.phone && <p className="pre-contact-phone">{item.phone}</p>}
                            {item.email && <p className="pre-contact-mail">{item.email}</p>}
                            {!item.phone && !item.email && <p className="pre-contact-mail">—</p>}
                          </div>
                        </td>

                        <td>
                          <div className="pre-docs">
                            <div className="pre-doc">
                              <ImageThumb
                                path={item.dni_front_path}
                                token={token}
                                alt="DNI anverso"
                                onOpen={openDocPreview}
                              />
                              {item.dni_front_path && <span className="pre-doc-label">Anverso</span>}
                            </div>
                            <div className="pre-doc">
                              <ImageThumb
                                path={item.dni_back_path}
                                token={token}
                                alt="DNI reverso"
                                onOpen={openDocPreview}
                              />
                              {item.dni_back_path && <span className="pre-doc-label">Reverso</span>}
                            </div>
                          </div>
                        </td>

                        <td className="pre-date">
                          {new Date(item.created_at).toLocaleDateString('es-ES')}
                        </td>

                        <td><StatusBadge status={item.status} /></td>

                        <td>
                          <RowActions
                            item={item}
                            onApprove={openCreateFromPreRegistration}
                            onReject={handleReject}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pre-pagination">
                <span className="pre-pagination-text">Página {currentPage} de {totalPages}</span>
                <div className="pre-pagination-nav">
                  <button
                    type="button" className="pre-page-btn" aria-label="Primera página"
                    disabled={currentPage === 1} onClick={() => setPage(1)}
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    type="button" className="pre-page-btn" aria-label="Página anterior"
                    disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button" className="pre-page-btn" aria-label="Página siguiente"
                    disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    type="button" className="pre-page-btn" aria-label="Última página"
                    disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {toast && (
        <div className={`pre-toast${toast.kind === 'error' ? ' pre-toast--error' : ''}`} role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          {toast.text}
        </div>
      )}

      <DocumentPreviewModal preview={docPreview} token={token} onClose={closeDocPreview} />

      {qrModalOpen && (
        <QRModal venueId={venueId} token={token} onClose={() => setQrModalOpen(false)} />
      )}

      {createModalOpen && (
        <CustomerModal
          active={createModalOpen}
          handleModal={() => {
            setCreateModalOpen(false);
            setPrefillClientData(null);
            setCreatingFromPreReg(null);
          }}
          token={token}
          id={null}
          prefillData={prefillClientData}
          shopId={venueId}
          onClientCreated={async (_client, meta) => {
            try {
              if (creatingFromPreReg) {
                await axios.put(
                  `/v1/venues/${venueId}/pre-registrations/${creatingFromPreReg.id}/approve`,
                  { expiry_date: meta?.expiration_date, notes: null },
                  { headers },
                );
              }
              setCreateModalOpen(false);
              setPrefillClientData(null);
              setCreatingFromPreReg(null);
              await fetchItems();
              showToast('Solicitud de inscripción aprobada');
            } catch {
              setError('El socio se creó, pero no se pudo marcar la preinscripción como aprobada.');
            }
          }}
        />
      )}
    </>
  );
}
